import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button,
    DatePicker,
    Empty,
    Input,
    Modal,
    Pagination,
    Select,
    Space,
    Table,
    Tag,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import {
    ExportOutlined,
    InfoCircleOutlined,
    SearchOutlined,
    SyncOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { ContentComponent } from '../../Components/ContentComponent/ContentComponent';
import { HeaderTitleComponent } from '../../Components/ContentComponent/HeaderTitleComponent';
import { Loader } from '../../Components/Loader/Loader';
import { SERVER_URL } from '../../utils/config';
import { getAuthenticatedFetchOptions, getLoginRequestHeader, Messages } from '../../utils';
import './CashRewardsPage.scss';

const { RangePicker } = DatePicker;

type CashRewardStatus =
    | 'IN_PROGRESS'
    | 'DELIVERED'
    | 'FAILED'
    | 'MANUALLY_DELIVERED'
    | 'ON_HOLD'
    | string;

interface ICashRewardTransaction {
    id: number;
    createdAt: string;
    createdBy?: {
        origin?: string;
        userId?: string;
    };
    brandId: number;
    source: string;
    requestId: string;
    transactionId: string;
    status: CashRewardStatus;
    playerId: string;
    currencyCode: string;
    amount: number;
    promotionType: string;
}

interface IPagingInfo {
    pageNumber: number;
    pageSize: number;
    sortBy: string;
    order: string;
}

interface ICashRewardsRequest {
    createdAtFrom: string | null;
    createdAtTo: string | null;
    brandId: number | null;
    source: string | null;
    requestId: string | null;
    transactionId: string | null;
    status: string | null;
    playerId: string | null;
    currencyCode: string | null;
    pagingInfo: IPagingInfo;
}

type CashRewardSearchKey =
    | 'transactionId'
    | 'requestId'
    | 'playerId'
    | 'source'
    | 'currencyCode'
    | 'brandId';

type CashRewardAction = 'resolve' | 'retry' | 'on-hold';

type EditableCashRewardStatus = 'IN_PROGRESS' | 'ON_HOLD' | 'MANUALLY_DELIVERED';

interface ICashRewardStatusChangeRequest {
    id: number;
    createdAt: string;
    status: string;
    totalCount: number;
    processedCount: number;
}

const PAGE_SIZE_OPTIONS = ['10', '20', '50', '100'];
const DEFAULT_PAGE_SIZE = 10;
const DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
const STATUS_CHANGE_POLL_INTERVAL = 2000;
const STATUS_CHANGE_MAX_POLL_ATTEMPTS = 30;
const FINAL_CASH_REWARD_STATUSES: CashRewardStatus[] = ['DELIVERED', 'MANUALLY_DELIVERED'];
const CASH_REWARD_STATUS_BY_ACTION: Record<CashRewardAction, EditableCashRewardStatus> = {
    resolve: 'MANUALLY_DELIVERED',
    retry: 'IN_PROGRESS',
    'on-hold': 'ON_HOLD',
};

const isFinalCashRewardStatus = (status: CashRewardStatus) =>
    FINAL_CASH_REWARD_STATUSES.includes(status);

const createInitialRequest = (): ICashRewardsRequest => ({
    createdAtFrom: dayjs().subtract(7, 'days').startOf('day').format(DATE_FORMAT),
    createdAtTo: dayjs().endOf('day').format(DATE_FORMAT),
    brandId: null,
    source: null,
    requestId: null,
    transactionId: null,
    status: null,
    playerId: null,
    currencyCode: null,
    pagingInfo: {
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        sortBy: 'createdAt',
        order: 'DESCENDING',
    },
});

const statusColor = (status: CashRewardStatus) => {
    switch (status) {
        case 'DELIVERED':
            return 'green';
        case 'MANUALLY_DELIVERED':
            return 'blue';
        case 'IN_PROGRESS':
            return 'orange';
        case 'FAILED':
            return 'red';
        case 'ON_HOLD':
            return 'gold';
        default:
            return 'default';
    }
};

const cashRewardsUrl = (request: ICashRewardsRequest) => {
    const params = new URLSearchParams();
    const { pagingInfo } = request;

    params.append('paging.pageNumber', pagingInfo.pageNumber.toString());
    params.append('paging.pageSize', pagingInfo.pageSize.toString());
    params.append('paging.sortBy', pagingInfo.sortBy);
    params.append('paging.order', pagingInfo.order);
    if (request.createdAtFrom) params.append('createdAtFrom', request.createdAtFrom);
    if (request.createdAtTo) params.append('createdAtTo', request.createdAtTo);
    if (request.brandId !== null) params.append('brandId', request.brandId.toString());
    if (request.source) params.append('source', request.source);
    if (request.requestId) params.append('requestId', request.requestId);
    if (request.transactionId) params.append('transactionId', request.transactionId);
    if (request.status) params.append('status', request.status);
    if (request.playerId) params.append('playerId', request.playerId);
    if (request.currencyCode) params.append('currencyCode', request.currencyCode);

    return `${SERVER_URL}/cash-rewards?${params.toString()}`;
};

const CashRewardsPage = () => {
    const [request, setRequest] = useState<ICashRewardsRequest>(createInitialRequest);
    const [transactions, setTransactions] = useState<ICashRewardTransaction[] | null>(null);
    const [total, setTotal] = useState(0);
    const [selectedCashRewardIds, setSelectedCashRewardIds] = useState<React.Key[]>([]);
    const [searchValue, setSearchValue] = useState('');
    const [searchKey, setSearchKey] = useState<CashRewardSearchKey>('transactionId');
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [cashRewardInfo, setCashRewardInfo] = useState<ICashRewardTransaction | null>(null);
    const [infoLoading, setInfoLoading] = useState(false);

    const loadTransactions = useCallback(async (body: ICashRewardsRequest) => {
        setTransactions(null);
        try {
            const response = await fetch(
                cashRewardsUrl(body),
                getAuthenticatedFetchOptions({
                    method: 'GET',
                    headers: {
                        ...getLoginRequestHeader(),
                        Accept: 'application/json',
                    },
                }),
            );

            if (!response.ok) {
                throw new Error('Failed to load cash reward transactions');
            }

            const responseBody = await response.json();
            const content = Array.isArray(responseBody)
                ? responseBody
                : responseBody.content || responseBody.data || [];
            setTransactions(content);
            setTotal(
                responseBody._metadata?.totalNumberOfElements ||
                    responseBody.totalElements ||
                    responseBody.totalNumberOfElements ||
                    content.length,
            );
            setSelectedCashRewardIds([]);
        } catch (error: any) {
            setTransactions([]);
            Messages.error(error.message);
        }
    }, []);

    useEffect(() => {
        loadTransactions(request);
    }, [loadTransactions, request]);

    const updateRequest = (changes: Partial<ICashRewardsRequest>) => {
        setRequest((current) => ({
            ...current,
            ...changes,
            pagingInfo: changes.pagingInfo || current.pagingInfo,
        }));
    };

    const handleStatusChange = (value: string) => {
        updateRequest({
            status: value === 'ALL' ? null : value,
            pagingInfo: { ...request.pagingInfo, pageNumber: 1 },
        });
    };

    const handleDateChange = (dates: null | [Dayjs | null, Dayjs | null]) => {
        updateRequest({
            createdAtFrom: dates?.[0]?.startOf('day').format(DATE_FORMAT) || null,
            createdAtTo: dates?.[1]?.endOf('day').format(DATE_FORMAT) || null,
            pagingInfo: { ...request.pagingInfo, pageNumber: 1 },
        });
    };

    const handleSearch = () => {
        const value = searchValue.trim();
        const searchFilters: Partial<ICashRewardsRequest> = {
            brandId: null,
            source: null,
            requestId: null,
            transactionId: null,
            playerId: null,
            currencyCode: null,
        };

        if (searchKey === 'brandId') {
            if (value && !/^\d+$/.test(value)) {
                Messages.error('Brand ID must be a number');
                return;
            }
            searchFilters.brandId = value ? Number(value) : null;
        } else {
            searchFilters[searchKey] = value || null;
        }

        updateRequest({
            ...searchFilters,
            pagingInfo: { ...request.pagingInfo, pageNumber: 1 },
        });
    };

    const handleSearchClear = () => {
        setSearchValue('');
        updateRequest({
            brandId: null,
            source: null,
            requestId: null,
            transactionId: null,
            playerId: null,
            currencyCode: null,
            pagingInfo: { ...request.pagingInfo, pageNumber: 1 },
        });
    };

    const handleSearchValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        if (!value) {
            handleSearchClear();
            return;
        }

        setSearchValue(value);
    };

    const handlePageChange = (pageNumber: number, pageSize: number) => {
        updateRequest({ pagingInfo: { ...request.pagingInfo, pageNumber, pageSize } });
    };

    const getStatusChangeRequest = async (id: number) => {
        const response = await fetch(
            `${SERVER_URL}/cash-reward-status-change-requests/${id}`,
            getAuthenticatedFetchOptions({
                method: 'GET',
                headers: {
                    ...getLoginRequestHeader(),
                    Accept: 'application/json',
                },
            }),
        );

        if (!response.ok) throw new Error('Failed to track cash reward status change request');

        return (await response.json()) as ICashRewardStatusChangeRequest;
    };

    const waitForStatusChangeRequest = async (id: number) => {
        for (let attempt = 0; attempt < STATUS_CHANGE_MAX_POLL_ATTEMPTS; attempt += 1) {
            await new Promise((resolve) => setTimeout(resolve, STATUS_CHANGE_POLL_INTERVAL));
            const statusChangeRequest = await getStatusChangeRequest(id);

            if (statusChangeRequest.status !== 'IN_PROGRESS') return statusChangeRequest;
        }

        return null;
    };

    const getResponseError = async (response: Response, fallback: string) => {
        try {
            const responseBody = await response.json();
            return responseBody.error?.message || fallback;
        } catch (_error) {
            return fallback;
        }
    };

    const handleBatchAction = async (
        action: CashRewardAction,
        selectedIds: React.Key[] = selectedCashRewardIds,
    ) => {
        const selectedFinalCashReward = selectedIds.find((selectedId) => {
            const cashReward =
                transactions?.find(({ id }) => String(id) === String(selectedId)) ||
                (String(cashRewardInfo?.id) === String(selectedId) ? cashRewardInfo : null);

            return cashReward && isFinalCashRewardStatus(cashReward.status);
        });

        if (selectedFinalCashReward !== undefined) {
            Messages.error('Delivered and manually delivered cash rewards cannot be changed');
            return;
        }

        setActionLoading(action);
        try {
            const cashRewardIds = selectedIds.map(Number);
            const response = await fetch(
                `${SERVER_URL}/cash-reward-status-change-requests`,
                getAuthenticatedFetchOptions({
                    method: 'POST',
                    headers: {
                        ...getLoginRequestHeader('application/json'),
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        cashRewardStatus: CASH_REWARD_STATUS_BY_ACTION[action],
                        cashRewardIds,
                    }),
                }),
            );

            if (!response.ok) {
                const fallback =
                    response.status === 409
                        ? 'One or more cash rewards are already processing. Please try again later.'
                        : `Failed to ${action.replace('-', ' ')} selected cash rewards`;
                throw new Error(await getResponseError(response, fallback));
            }

            const createdRequest = (await response.json()) as ICashRewardStatusChangeRequest;
            Messages.success(
                `Status change request #${createdRequest.id} created for ${createdRequest.totalCount} cash rewards`,
            );

            const completedRequest = await waitForStatusChangeRequest(createdRequest.id);
            if (!completedRequest) {
                Messages.success(
                    `Status change request #${createdRequest.id} is still processing in the background`,
                );
            } else if (completedRequest.status === 'FAILED') {
                throw new Error(`Status change request #${completedRequest.id} failed`);
            } else {
                Messages.success(
                    `Status change request #${completedRequest.id} completed: ${completedRequest.processedCount}/${completedRequest.totalCount} processed`,
                );
            }

            setSelectedCashRewardIds([]);
            await loadTransactions(request);
        } catch (error: any) {
            Messages.error(error.message);
        } finally {
            setActionLoading(null);
        }
    };

    const showCashRewardInfo = async (cashReward: ICashRewardTransaction) => {
        if (cashReward.id === undefined) {
            Messages.error('Cash reward ID is missing');
            return;
        }

        setInfoLoading(true);
        try {
            const response = await fetch(
                `${SERVER_URL}/cash-rewards/${cashReward.id}`,
                getAuthenticatedFetchOptions({
                    method: 'GET',
                    headers: {
                        ...getLoginRequestHeader(),
                        Accept: 'application/json',
                    },
                }),
            );

            if (!response.ok) throw new Error('Failed to load cash reward details');
            const responseBody = await response.json();
            setCashRewardInfo(responseBody.data || responseBody);
        } catch (error: any) {
            Messages.error(error.message);
        } finally {
            setInfoLoading(false);
        }
    };

    const handleExport = async () => {
        setActionLoading('export');
        try {
            const response = await fetch(
                cashRewardsUrl(request),
                getAuthenticatedFetchOptions({
                    method: 'GET',
                    headers: {
                        ...getLoginRequestHeader(),
                        Accept: 'text/csv',
                    },
                }),
            );

            if (!response.ok) {
                throw new Error('Failed to export cash reward transactions');
            }

            const disposition = response.headers.get('Content-Disposition');
            const filename = disposition?.match(/filename="?([^";]+)"?/)?.[1] || 'cash-rewards.csv';
            const url = window.URL.createObjectURL(await response.blob());
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = filename;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            Messages.error(error.message);
        } finally {
            setActionLoading(null);
        }
    };

    const columns: ColumnsType<ICashRewardTransaction> = useMemo(
        () => [
            {
                title: 'Info',
                key: 'info',
                width: 64,
                render: (_value, row) => (
                    <Button
                        aria-label="View cash reward information"
                        type="text"
                        loading={infoLoading}
                        icon={<InfoCircleOutlined />}
                        onClick={() => showCashRewardInfo(row)}
                    />
                ),
            },
            {
                title: 'Created At',
                dataIndex: 'createdAt',
                sorter: true,
                sortOrder:
                    request.pagingInfo.order === 'ASCENDING' ? 'ascend' : 'descend',
                sortDirections: ['ascend', 'descend', 'ascend'],
            },
            {
                title: 'Status',
                dataIndex: 'status',
                render: (status: CashRewardStatus) => (
                    <Tag color={statusColor(status)}>{status.replaceAll('_', ' ')}</Tag>
                ),
            },
            {
                title: 'Amount',
                dataIndex: 'amount',
                render: (amount, row) => `${amount} ${row.currencyCode}`,
            },
            { title: 'Brand ID', dataIndex: 'brandId' },
            { title: 'Source', dataIndex: 'source' },
            { title: 'Request ID', dataIndex: 'requestId' },
            { title: 'Transaction ID', dataIndex: 'transactionId' },
            { title: 'Player ID', dataIndex: 'playerId' },
            { title: 'Promotion Type', dataIndex: 'promotionType' },
        ],
        [infoLoading, request.pagingInfo.order],
    );

    const handleTableChange = (_pagination: TablePaginationConfig, _filters: any, sorter: any) => {
        if (sorter.field !== 'createdAt' || !sorter.order) return;
        updateRequest({
            pagingInfo: {
                ...request.pagingInfo,
                pageNumber: 1,
                sortBy: 'createdAt',
                order: sorter.order === 'ascend' ? 'ASCENDING' : 'DESCENDING',
            },
        });
    };

    const hasSelection = selectedCashRewardIds.length > 0;
    const infoRows = cashRewardInfo
        ? [
              ['Cash Reward ID', cashRewardInfo.id],
              ['Created At', cashRewardInfo.createdAt],
              ['Created By Origin', cashRewardInfo.createdBy?.origin],
              ['Created By User ID', cashRewardInfo.createdBy?.userId],
              ['Brand ID', cashRewardInfo.brandId],
              ['Source', cashRewardInfo.source],
              ['Request ID', cashRewardInfo.requestId],
              ['Transaction ID', cashRewardInfo.transactionId],
              ['Status', cashRewardInfo.status],
              ['Player ID', cashRewardInfo.playerId],
              ['Currency Code', cashRewardInfo.currencyCode],
              ['Amount', cashRewardInfo.amount],
              ['Promotion Type', cashRewardInfo.promotionType],
          ]
        : [];

    return (
        <ContentComponent
            header={
                <>
                    <HeaderTitleComponent title="Cash Rewards" customBreadcrumbs={<div>All</div>} />
                    <Space wrap className="cash-rewards__toolbar mt-2">
                        <Button
                            aria-label="Refresh cash rewards"
                            icon={<SyncOutlined />}
                            onClick={() => loadTransactions(request)}
                        />
                        <RangePicker
                            defaultValue={[dayjs().subtract(7, 'days'), dayjs()]}
                            onChange={handleDateChange}
                        />
                        <Select
                            className="cash-rewards__status-filter"
                            defaultValue="ALL"
                            popupMatchSelectWidth={200}
                            onChange={handleStatusChange}
                        >
                            <Select.Option value="ALL">All</Select.Option>
                            <Select.Option value="IN_PROGRESS">In Progress</Select.Option>
                            <Select.Option value="DELIVERED">Delivered</Select.Option>
                            <Select.Option value="FAILED">Failed</Select.Option>
                            <Select.Option value="MANUALLY_DELIVERED">
                                Manually Delivered
                            </Select.Option>
                            <Select.Option value="ON_HOLD">On Hold</Select.Option>
                        </Select>
                        <Button
                            icon={<ExportOutlined />}
                            loading={actionLoading === 'export'}
                            onClick={handleExport}
                        >
                            Export to CSV
                        </Button>
                        <Button
                            disabled={!hasSelection}
                            loading={actionLoading === 'resolve'}
                            onClick={() => handleBatchAction('resolve')}
                        >
                            Resolve Selected
                        </Button>
                        <Button
                            disabled={!hasSelection}
                            loading={actionLoading === 'retry'}
                            onClick={() => handleBatchAction('retry')}
                        >
                            Retry Selected
                        </Button>
                        <Button
                            disabled={!hasSelection}
                            loading={actionLoading === 'on-hold'}
                            onClick={() => handleBatchAction('on-hold')}
                        >
                            Put On Hold Selected
                        </Button>
                    </Space>
                    <div className="cash-rewards__search-row">
                        <Space.Compact className="cash-rewards__search">
                            <Select value={searchKey} onChange={setSearchKey}>
                                <Select.Option value="transactionId">Transaction ID</Select.Option>
                                <Select.Option value="requestId">Request ID</Select.Option>
                                <Select.Option value="playerId">Player ID</Select.Option>
                                <Select.Option value="source">Source</Select.Option>
                                <Select.Option value="currencyCode">Currency</Select.Option>
                                <Select.Option value="brandId">Brand ID</Select.Option>
                            </Select>
                            <Input
                                allowClear
                                value={searchValue}
                                placeholder="Enter search value"
                                onChange={handleSearchValueChange}
                                onPressEnter={handleSearch}
                                suffix={<SearchOutlined onClick={handleSearch} />}
                            />
                        </Space.Compact>
                        <Pagination
                            current={request.pagingInfo.pageNumber}
                            pageSize={request.pagingInfo.pageSize}
                            pageSizeOptions={PAGE_SIZE_OPTIONS}
                            total={total}
                            showSizeChanger
                            onChange={handlePageChange}
                        />
                    </div>
                </>
            }
            innerContent={
                <>
                    {transactions === null ? (
                        <Loader style={{ height: '70vh' }} />
                    ) : transactions.length ? (
                        <Table<ICashRewardTransaction>
                            rowKey="id"
                            columns={columns}
                            dataSource={transactions}
                            pagination={false}
                            scroll={{ x: 1300 }}
                            onChange={handleTableChange}
                            rowSelection={{
                                selectedRowKeys: selectedCashRewardIds,
                                onChange: setSelectedCashRewardIds,
                                getCheckboxProps: (cashReward) => ({
                                    disabled: isFinalCashRewardStatus(cashReward.status),
                                    name: `cash-reward-${cashReward.id}`,
                                }),
                            }}
                        />
                    ) : (
                        <Empty description="No cash reward transactions" />
                    )}
                    <Modal
                        title="Info"
                        open={cashRewardInfo !== null}
                        onCancel={() => setCashRewardInfo(null)}
                        footer={[
                            <Button
                                key="resolve"
                                disabled={
                                    !!cashRewardInfo &&
                                    isFinalCashRewardStatus(cashRewardInfo.status)
                                }
                                loading={actionLoading === 'resolve'}
                                onClick={() => {
                                    if (!cashRewardInfo) return;
                                    handleBatchAction('resolve', [cashRewardInfo.id]);
                                }}
                            >
                                Resolve Manually
                            </Button>,
                            <Button key="ok" type="primary" onClick={() => setCashRewardInfo(null)}>
                                OK
                            </Button>,
                        ]}
                    >
                        <div className="cash-rewards-info">
                            {infoRows.map(([label, value]) => (
                                <div className="cash-rewards-info__row" key={String(label)}>
                                    <span>{label}</span>
                                    {label === 'Status' && value ? (
                                        <Tag color={statusColor(String(value))}>
                                            {String(value).replaceAll('_', ' ')}
                                        </Tag>
                                    ) : (
                                        <strong>
                                            {value === undefined || value === null ? 'n/a' : value}
                                        </strong>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Modal>
                </>
            }
        />
    );
};

export default CashRewardsPage;
